import { Eye, Package, Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { useState } from 'react'

import { StockLevelItem } from '@/hooks/features/stock'

import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const getStatusBadge = (status: StockLevelItem['status']) => {
  const map: Record<StockLevelItem['status'], { label: string; tone: BadgeTone }> = {
    'in-stock': { label: 'In stock', tone: 'success' },
    'low-stock': { label: 'Low stock', tone: 'warning' },
    'out-of-stock': { label: 'Out of stock', tone: 'error' },
  }
  const { label, tone } = map[status]
  return <Badge tone={tone}>{label}</Badge>
}

interface Props {
  items: StockLevelItem[]
  loading: boolean
}

export const StockLevels = ({ items, loading }: Props) => {
  const [search, setSearch] = useState('')
  const navigate = useNavigate()

  const filtered = items.filter(
    (item) =>
      item.productName.toLowerCase().includes(search.toLowerCase()) ||
      item.sku.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div>
      <div className="p-3">
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by product or SKU"
            aria-label="Search by product or SKU"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <div className="border-t border-border-subtle">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead className="hidden md:table-cell">SKU</TableHead>
              <TableHead className="text-right">Available</TableHead>
              <TableHead className="hidden text-right md:table-cell">Minimum</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <TableCell key={j} className={j === 1 || j === 3 ? 'hidden md:table-cell' : undefined}>
                      <Skeleton className="h-4 w-3/4" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  <Package className="mx-auto mb-2 size-8 text-faint" aria-hidden="true" />
                  <p className="text-sm">No items found</p>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((item) => (
                <TableRow key={item._id}>
                  <TableCell>
                    <div className="font-medium">{item.productName}</div>
                    {/* Phones drop the SKU column, so it sits under the name. */}
                    <div className="text-xs text-muted-foreground md:hidden">{item.sku}</div>
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground md:table-cell">{item.sku}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span
                      className={
                        item.quantity <= item.minStockQuantity
                          ? 'font-medium text-warning-dark'
                          : ''
                      }
                    >
                      {item.quantity}
                    </span>
                  </TableCell>
                  <TableCell className="hidden text-right tabular-nums text-muted-foreground md:table-cell">
                    {item.minStockQuantity}
                  </TableCell>
                  <TableCell>{getStatusBadge(item.status)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="View products"
                      onClick={() => navigate(`/products`)}
                    >
                      <Eye />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
